import { capitalize, handleize } from '@utilities/theme';
import { EVENTS, publish, subscribe } from '@utilities/events';

/**
 * ProductOptionSelectors
 * Manages all product option interaction: standard radio options, color swatches,
 * delimited options, option availability, color labels, inventory alerts, and
 * TrueFit size recommendation integration.
 *
 * Relies on its parent <product-overview> for product/variant state and data fetching.
 * Dispatches events upward so <product-overview> can orchestrate data updates.
 *
 * Listens for:
 *   - product-overview:initialized
 *   - product-overview:variant-change
 *   - product-overview:no-variant
 *
 * Dispatches (handled by product-overview):
 *   - change / swatch:change  (native DOM events that bubble to product-overview)
 */
if (!customElements.get('product-option-selectors')) {
  customElements.define('product-option-selectors', class ProductOptionSelectors extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement|null} */
      this.productOverview = null;

      // Swatch state
      this.selectedSwatchData = {};
      this.selectedSwatchElement = null;

      // Bound handlers
      this.boundHandleInitialized = this.handleInitialized.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleNoVariant = this.handleNoVariant.bind(this);
      this.boundHandleOptionChange = this.handleOptionChange.bind(this);
      this.boundHandleTruefitRendered = this.setupTrueFitHandlers.bind(this);
    }

    // ─── Debug ──────────────────────────────────────────────
    allowDebug() {
      return this.productOverview?.classList.contains('debug') ?? false;
    }

    // ─── Lifecycle ──────────────────────────────────────────
    connectedCallback() {
      this.productOverview = this.closest('product-overview');
      if (!this.productOverview) {
        console.warn('ProductOptionSelectors: No <product-overview> parent found.');
        return;
      }

      this.cacheElements();

      // If parent already initialised, run immediately
      if (this.productOverview.classList.contains('product-overview--initialized')) {
        this.onReady(this.productOverview.product, this.productOverview.variant);
      }

      this.subscribeToEvents();
    }

    disconnectedCallback() {
      this.unsubscribeFromEvents();
      this.productOverview = null;
    }

    // ─── Element Cache ──────────────────────────────────────
    cacheElements() {
      this.colorElement = this.querySelector('.product__option-name__selected--color');
      this.swatchContainer = this.querySelector('.js-product-swatches');
      this.swatches = this.querySelectorAll('.js-product-swatches input');
      this.optionGroups = this.querySelectorAll('.js-product-radios');
      this.nonColorGroups = this.querySelectorAll('.js-product-radios:not(.js-product-swatches)');
      this.delimitedOptions = this.querySelectorAll('.js-product-delimited-option');
      this.delimitedOptionGroups = this.querySelectorAll('.js-product-delimited-radios');
      // Inventory alert lives within the options area
      this.inventoryAlertContainer = this.productOverview?.querySelector('.product__inventory-alert-container') ?? null;
    }

    // ─── Subscriptions ─────────────────────────────────────
    subscribeToEvents() {
      this.unsubscribers = [
        subscribe(EVENTS.PRODUCT_OVERVIEW_INITIALIZED, this.boundHandleInitialized),
        subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, this.boundHandleVariantChange),
        subscribe(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, this.boundHandleNoVariant),
      ];

      // Native DOM events stay as-is
      this.addEventListener('change', this.boundHandleOptionChange);
      document.addEventListener('tfc:widget-rendered', this.boundHandleTruefitRendered);
      this.setupTrueFitHandlers();
    }

    unsubscribeFromEvents() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
      this.removeEventListener('change', this.boundHandleOptionChange);
      document.removeEventListener('tfc:widget-rendered', this.boundHandleTruefitRendered);
    }

    // ─── Event Handlers ─────────────────────────────────────
    handleInitialized(data) {
      if (data.productOverview !== this.productOverview) return;
      this.onReady(data.product, data.variant);
    }

    handleVariantChange(data) {
      if (data.productOverview !== this.productOverview) return;
      const { variant } = data;
      const product = this.productOverview?.product;

      // Update option availability from the product data
      this.updateOptionsAvailability(product);

      // Update color label
      this.updateColorLabel(variant);

      if (!variant || Object.keys(variant).length === 0) {
        this.updateInventoryAlert('');
        return;
      }

      this.updateInventoryAlert(variant.inventory_alert_message);
    }

    handleNoVariant(data) {
      if (data.productOverview !== this.productOverview) return;
      this.updateInventoryAlert('');
    }

    // ─── Initial render when parent is ready ────────────────
    onReady(product, variant) {
      if (product?.available) {
        this.setSelectedSwatchData(product);
        this.updateOptionsAvailability(product);
        this.updateColorLabel(variant);
      }
      if (variant && Object.keys(variant).length > 0) {
        this.updateInventoryAlert(variant.inventory_alert_message);
      }
    }

    // ─── Option Change Routing ──────────────────────────────
    /**
     * Routes change events to the correct handler based on the input's context.
     * After processing, dispatches an event that product-overview catches to
     * trigger data fetching and state updates.
     */
    async handleOptionChange(event) {
      const target = event.target;

      // Handle delimited option changes
      const delimitedGroup = target.closest('.js-product-delimited-radios');
      if (delimitedGroup && target.classList.contains('js-variant-delimited-option')) {
        event.preventDefault();

        const delimitedOption = delimitedGroup.dataset.option;
        const result = this.calculateDelimitedOption(delimitedOption);

        if (!result.valid) {
          publish(EVENTS.PRODUCT_OPTION_SELECTORS_INVALID_OPTION, {
            productOverview: this.productOverview,
            reason: 'delimited-incomplete',
          });
          return;
        }

        publish(EVENTS.PRODUCT_OPTION_SELECTORS_OPTION_CHANGE, {
          productOverview: this.productOverview,
          optionValueIds: this.getSelectedOptionValueIds(),
        });
        return;
      }

      // Handle standard (non-swatch) option changes
      if (target.closest('.js-product-radios:not(.js-product-swatches)')) {
        event.preventDefault();
        publish(EVENTS.PRODUCT_OPTION_SELECTORS_OPTION_CHANGE, {
          productOverview: this.productOverview,
          optionValueIds: this.getSelectedOptionValueIds(),
        });
        return;
      }

      // Handle swatch changes
      if (target.closest('.js-product-radios.js-product-swatches')) {
        event.preventDefault();
        await this.handleSwatchChange(target);
        return;
      }
    }

    // ─── Swatch Handling ────────────────────────────────────
    async handleSwatchChange(swatch) {
      if (!swatch) {
        if (this.allowDebug()) console.log('ProductOptionSelectors: No swatch data sent');
        return;
      }

      const product = this.productOverview?.product;
      if (!product) return;

      const optionValueId = swatch.dataset.optionValueId;
      const handleizedColor = swatch.dataset.jsHandle;
      const matchingSwatch = product.swatches?.find((s) => {
        if (optionValueId) {
          return String(s.option_value_id) === String(optionValueId);
        }
        return s.handle === handleizedColor;
      });

      if (!matchingSwatch) {
        if (this.allowDebug()) console.log('ProductOptionSelectors: No matching swatch found');
        return;
      }

      this.selectedSwatchData = matchingSwatch;
      this.selectedSwatchElement = swatch;

      publish(EVENTS.PRODUCT_OPTION_SELECTORS_SWATCH_CHANGE, {
        productOverview: this.productOverview,
        swatch,
        swatchData: matchingSwatch,
        optionValueIds: this.getSelectedOptionValueIds(),
        activeProductUrl: matchingSwatch.url ?? swatch.dataset.jsProductUrl ?? null,
      });
    }

    /**
     * Sets the initial swatch data based on the currently checked swatch in the DOM.
     */
    setSelectedSwatchData(product) {
      this.selectedSwatchElement = null;
      this.selectedSwatchData = {};

      if (!this.hasSwatches(product)) {
        if (this.allowDebug()) {
          console.log('ProductOptionSelectors: setSelectedSwatchData() - no swatches for the given section.');
        }
        return;
      }

      const selectedSwatchElement = this.swatchContainer?.querySelector('input:checked');
      if (!selectedSwatchElement) {
        if (this.allowDebug()) {
          console.log('ProductOptionSelectors: setSelectedSwatchData() - no default selected swatch.');
        }
        const firstSwatch = this.swatchContainer?.querySelector('input:first-of-type');
        if (firstSwatch) firstSwatch.click();
        return;
      }

      this.selectedSwatchElement = selectedSwatchElement;
      const optionValueId = selectedSwatchElement.dataset.optionValueId;
      const handleizedColor = selectedSwatchElement.dataset.jsHandle;

      const matchingSwatch = product.swatches?.find((swatch) => {
        if (optionValueId) {
          return String(swatch.option_value_id) === String(optionValueId);
        }
        return swatch.handle === handleizedColor;
      });

      if (!matchingSwatch) {
        if (this.allowDebug()) {
          console.log('ProductOptionSelectors: setSelectedSwatchData() - no matching swatch in product data.');
        }
        return;
      }

      this.selectedSwatchData = matchingSwatch;
      this.updateColorLabel(this.productOverview?.variant);
    }

    hasSwatches(product) {
      return this.swatchContainer && product?.swatches?.length > 0;
    }

    // ─── Color Label ────────────────────────────────────────
    updateColorLabel(variant) {
      if (!this.colorElement) return;

      const swatchName = this.selectedSwatchData?.name ?? '';
      const swatchFamily = this.selectedSwatchData?.color_family ?? '';
      const variantName = variant?.color ?? '';
      const variantFamily = variant?.color_family ?? '';

      const name = (swatchName || variantName || '').toString().trim();
      const family = (swatchFamily || variantFamily || '').toString().trim();

      if (!name && !family) return;

      if (family) {
        const nameText = document.createTextNode(`${capitalize(name.toLowerCase())} `);
        const familySpan = document.createElement('span');
        familySpan.className = 'product__option-name__selected__color-family';
        familySpan.textContent = capitalize(family.toLowerCase());
        this.colorElement.replaceChildren(nameText, familySpan);
      } else {
        this.colorElement.textContent = capitalize(name.toLowerCase());
      }
    }

    // ─── Inventory Alert ────────────────────────────────────
    updateInventoryAlert(html) {
      if (!this.inventoryAlertContainer) return;
      if (!html) {
        this.inventoryAlertContainer.replaceChildren();
        return;
      }
      const template = document.createElement('template');
      template.innerHTML = html;
      this.inventoryAlertContainer.replaceChildren(...template.content.childNodes);
    }

    // ─── Option Availability ────────────────────────────────
    updateOptionsAvailability(product) {
      if (product?.options) {
        this.updateOptionsFromData(product.options);
      }

      publish(EVENTS.PRODUCT_OPTION_SELECTORS_OPTION_AVAILABILITY_CHANGE, {
        productOverview: this.productOverview,
      });
    }

    /**
     * Updates option availability state on DOM elements from the options JSON data.
     * Uses `variant` and `variant_available` fields for combo-aware availability.
     */
    updateOptionsFromData(optionsData) {
      if (!optionsData || !Array.isArray(optionsData)) return;

      if (this.allowDebug()) {
        console.log('ProductOptionSelectors: updateOptionsFromData()', optionsData);
      }

      for (const option of optionsData) {
        if (!option.values || !Array.isArray(option.values)) continue;

        for (const value of option.values) {
          const optionValueId = String(value.id);

          // Search within this element and also at the product-overview level
          // (swatches may live inside this element)
          const searchRoot = this.productOverview || this;
          const matchingInputs = searchRoot.querySelectorAll(
            `.js-variant-option[data-option-value-id="${optionValueId}"]`
          );

          for (const input of matchingInputs) {
            const isImpossible = value.variant === null;
            const isUnavailable =
              value.available === false ||
              (value.variant !== null && value.variant_available === false);

            this.updateOptionClasses(input, isImpossible, isUnavailable);

            input.dataset.optionAvailable = String(!isImpossible && !isUnavailable);
            input.dataset.optionVariant = value.variant !== null ? String(value.variant) : '';
          }
        }
      }
    }

    updateOptionClasses(element, impossible, unavailable) {
      element.classList.toggle('impossible-combination', impossible);
      element.classList.toggle('option-unavailable', unavailable && !impossible);

      if (impossible) {
        element.setAttribute('tabindex', '-1');
      } else {
        element.removeAttribute('tabindex');
      }
    }

    // ─── Delimited Options ──────────────────────────────────
    calculateDelimitedOption(delimitedOption) {
      if (!delimitedOption) return { valid: false };

      const searchRoot = this.productOverview || this;
      const container = searchRoot.querySelector(
        `.js-product-delimited-option[data-option="${delimitedOption}"]`
      );
      if (!container) return { valid: false };

      const selectedParts = new Map();
      container
        .querySelectorAll('.js-variant-delimited-option:checked')
        .forEach((selected) => {
          const group = selected.closest('.js-product-delimited-radios');
          if (group?.dataset.optionMap) {
            selectedParts.set(group.dataset.optionMap, selected.value);
          }
        });

      if (this.allowDebug()) {
        console.log(`ProductOptionSelectors: calculateDelimitedOption parts for ${delimitedOption}:`, selectedParts);
      }

      const matchingVariantOption = Array.from(
        container.querySelectorAll('.js-variant-option')
      ).find((option) =>
        Array.from(selectedParts.entries()).every(
          ([attr, value]) => option.getAttribute(attr) === value
        )
      );

      if (!matchingVariantOption) {
        if (this.allowDebug()) {
          console.log(`ProductOptionSelectors: no matching complete option found for ${delimitedOption}`);
        }
        return { valid: false };
      }

      const currentlySelected = container.querySelector('.js-variant-option:checked');
      if (currentlySelected !== matchingVariantOption) {
        if (currentlySelected) currentlySelected.checked = false;
        matchingVariantOption.checked = true;
      }

      return { valid: true };
    }

    // ─── Selection Getters ──────────────────────────────────
    getSelectedOptions() {
      const searchRoot = this.productOverview || this;
      return searchRoot.querySelectorAll('.js-variant-option:checked');
    }

    getSelectedOptionValueIds() {
      const groups = Array.from(this.optionGroups || []).sort((a, b) => {
        const aKey = parseInt(a.dataset.optionKey.replace('option', ''), 10);
        const bKey = parseInt(b.dataset.optionKey.replace('option', ''), 10);
        return aKey - bKey;
      });

      const selectedIds = [];

      for (const group of groups) {
        const isDelimited = group.classList.contains('js-product-delimited-option');
        const selectedInput = isDelimited
          ? group.querySelector('.js-variant-option:checked')
          : group.querySelector('input[type="radio"]:checked');
        const optionValueId = selectedInput?.dataset?.optionValueId;
        if (optionValueId) {
          selectedIds.push(optionValueId);
        }
      }

      return selectedIds;
    }

    // ─── Swatch Getters ─────────────────────────────────────
    getSelectedSwatchElement() {
      return this.selectedSwatchElement ?? null;
    }

    getSelectedSwatchData() {
      return this.selectedSwatchData ?? {};
    }

    // ─── TrueFit Integration ────────────────────────────────
    setupTrueFitHandlers() {
      try {
        const searchRoot = this.productOverview || this;
        [...searchRoot.querySelectorAll('[data-tfc-action="selectSize"]')].forEach((button) => {
          button.onclick = () => {
            if (button.innerText.trim() && button.innerText.trim().length > 0) {
              this.selectTrueFitSize({ suggested: button.innerText.trim() });
            }
          };
        });
      } catch (error) {
        console.error('ProductOptionSelectors: Trufit size selection error:', error);
      }
    }

    selectTrueFitSize(sizes) {
      if (!sizes) return;

      const searchRoot = this.productOverview || this;
      if (!searchRoot.querySelectorAll('.js-product-radios').length) return;

      const sizeGroup = searchRoot.querySelector('.js-product-radios[data-option="Size"]');
      if (!sizeGroup) return;

      const sizeSuggested = handleize(sizes.suggested);
      const sizeAlternative = sizes.alternative ? handleize(sizes.alternative) : null;

      const sizeInputs = sizeGroup.querySelectorAll('input');
      sizeInputs.forEach((sizeInput) => {
        const sizeInputValue = handleize(sizeInput.value);
        if (sizeInputValue === sizeSuggested || (sizeAlternative && sizeInputValue === sizeAlternative)) {
          sizeInput.checked = true;
          sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  });
}
